import * as Headless from '@headlessui/react';
import { clsx } from 'clsx';
import React from 'react';
import type { TimeCardShift } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/dropdown';
import { TooltippedText } from '../../../components/tooltipped-text';
import { formatDate, formatDuration, formatTime } from '../../../utils/date';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import {
  SHIFT_TYPE_BORDER_COLORS,
  SHIFT_TYPE_ICON_COLORS,
  SHIFT_TYPE_ICONS,
} from '../../../utils/utils';

type ShiftMobileCardProps = {
  shift: TimeCardShift;
  canEdit: boolean;
  showFacility: boolean;
  primaryFacilityName?: string;
  shiftTypeLabels: Record<string, string>;
  removalReasonLabels: Record<string, string>;
  onEdit: (shift: TimeCardShift) => void;
  onRemove: (shift: TimeCardShift) => void;
  onRestore: (shift: TimeCardShift) => void;
  onHistory: (shift: TimeCardShift) => void;
  onDocuments: (shift: TimeCardShift) => void;
};

/* eslint-disable sonarjs/cognitive-complexity */
export const ShiftMobileCard: React.FC<ShiftMobileCardProps> = ({
  shift,
  canEdit,
  showFacility,
  primaryFacilityName,
  shiftTypeLabels,
  removalReasonLabels,
  onEdit,
  onRemove,
  onRestore,
  onHistory,
  onDocuments,
}) => {
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });
  const isRemoved = shift.is_removed;
  const rateType = shift.shift_rate_type as keyof typeof SHIFT_TYPE_ICONS;

  const borderColor = isRemoved
    ? 'tw:border-l-zinc-300'
    : SHIFT_TYPE_BORDER_COLORS[rateType] || 'tw:border-l-zinc-300';
  const iconColor = SHIFT_TYPE_ICON_COLORS[rateType] || '';
  const iconClass = SHIFT_TYPE_ICONS[rateType] || '';

  const amberBadge = 'tw:bg-amber-100 tw:text-amber-700';
  const modBadges: Array<{ label: string; classes: string }> = [];
  if (shift.is_added)
    modBadges.push({ label: 'Added', classes: 'tw:bg-green-100 tw:text-green-700' });
  if (shift.is_facility_changed) modBadges.push({ label: 'Facility Changed', classes: amberBadge });
  if (shift.is_extended) modBadges.push({ label: 'Extended', classes: amberBadge });
  if (shift.is_reduced) modBadges.push({ label: 'Reduced', classes: amberBadge });

  const showBonus = Number(shift.bonus_amount ?? 0) > 0;
  const showHoliday = Number(shift.holiday_amount ?? 0) > 0;
  const showReason = !!(isRemoved && shift.removal_reason);
  const showExplanation = !!shift.explanation;
  const showFacilityMismatchIcon = !!(
    primaryFacilityName && shift.facility?.name !== primaryFacilityName
  );

  return (
    <div
      className={clsx(
        'tw:bg-zinc-50 tw:rounded-lg tw:border tw:border-zinc-200 tw:border-l-6 tw:p-3 tw:shadow-sm tw:flex tw:flex-col tw:gap-2 tw:select-none',
        borderColor,
        isRemoved && 'tw:opacity-60'
      )}
    >
      {/* Headline 1: rate icon + date/time + actions */}
      <div className="tw:flex tw:justify-between tw:items-start tw:gap-2">
        <div
          className={clsx(
            'tw:flex tw:items-start tw:gap-2 tw:min-w-0',
            isRemoved && 'tw:line-through'
          )}
        >
          {iconClass && <i className={clsx('fas tw:text-lg tw:shrink-0', iconClass, iconColor)} />}
          <div className="tw:min-w-0">
            <div className="tw:text-sm tw:font-medium tw:break-words">
              {formatDate(shift.start_on)}
              {shift.end_on && shift.end_on !== shift.start_on && (
                <> – {formatDate(shift.end_on)}</>
              )}
            </div>
            <div className="tw:text-sm tw:text-zinc-500">
              {formatTime(shift.start_time)} – {formatTime(shift.end_time)}
            </div>
          </div>
        </div>

        <div className="tw:flex tw:items-center tw:gap-2 tw:shrink-0">
          <button
            type="button"
            className="tw:relative tw:text-zinc-500 tw:hover:text-zinc-700 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-2 tw:-m-2"
            onClick={() => onDocuments(shift)}
            title="Shift documents"
            aria-label="Shift documents"
          >
            <i className="fas fa-paperclip tw:text-base" />
            {(shift.document_count ?? 0) > 0 && (
              <span className="tw:absolute tw:top-0 tw:right-0 tw:bg-brand-green-0 tw:text-white tw:text-[9px] tw:leading-none tw:font-medium tw:rounded-full tw:min-w-[14px] tw:h-[14px] tw:flex tw:items-center tw:justify-center tw:px-0.5">
                {shift.document_count}
              </span>
            )}
          </button>

          {canEdit && (
            <button
              type="button"
              className="tw:text-zinc-500 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-2 tw:-m-2"
              onClick={() => onEdit(shift)}
              title="Edit shift"
              aria-label="Edit shift"
            >
              <i className="fas fa-pencil tw:text-base" />
            </button>
          )}

          <Dropdown>
            <DropdownButton
              as="button"
              className="tw:text-zinc-500 tw:hover:text-zinc-700 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-2 tw:-m-2"
              aria-label="More actions"
            >
              <i className="fas fa-ellipsis-vertical tw:text-base" />
            </DropdownButton>
            <Headless.Portal>
              <DropdownMenu anchor="bottom end" className="tw:z-[70]">
                <DropdownItem onClick={() => onHistory(shift)}>
                  <i data-slot="icon" className="fas fa-fw fa-clock-rotate-left" />
                  History
                </DropdownItem>
                {canEdit && !isRemoved && (
                  <DropdownItem onClick={() => onRemove(shift)}>
                    <i data-slot="icon" className="fas fa-fw fa-xmark tw:!text-stoplight-danger" />
                    <span className="tw:text-stoplight-danger">Remove</span>
                  </DropdownItem>
                )}
                {canEdit && isRemoved && (
                  <DropdownItem onClick={() => onRestore(shift)}>
                    <i data-slot="icon" className="fas fa-fw fa-rotate-left" />
                    Restore
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Headless.Portal>
          </Dropdown>
        </div>
      </div>

      {/* Meta row: facility (multi-facility timecards only) */}
      {showFacility && (
        <div className="tw:text-sm">
          <div className="tw:flex tw:items-center tw:gap-1">
            {showFacilityMismatchIcon && <i className="far fa-info-circle tw:text-blue-600" />}
            <i className="fas fa-hospital tw:text-zinc-500" />
            <span>{shift.facility?.name}</span>
          </div>
          <div className="tw:text-xs tw:text-zinc-500 tw:ml-5">
            {getFacilityLocationDisplayValue(shift.facility)}
          </div>
        </div>
      )}

      {/* Headline 2: rate label · duration / total amount */}
      <div
        className={clsx(
          'tw:flex tw:justify-between tw:items-baseline tw:gap-2',
          isRemoved && 'tw:line-through'
        )}
      >
        <div className="tw:text-sm tw:text-zinc-600">
          {shiftTypeLabels[shift.shift_rate_type] || shift.shift_rate_type}
          {shift.duration && <> · {formatDuration(shift.duration)}</>}
        </div>
        <div className="tw:text-base tw:font-semibold">
          {formatCurrencyPostfix(shift.total_amount)}
        </div>
      </div>

      {/* Reason + Note section (full width, no labels) */}
      {(showReason || showExplanation) && (
        <div className="tw:flex tw:flex-col tw:gap-0">
          {showReason && (
            <div className="tw:text-sm tw:italic tw:text-zinc-600">
              {removalReasonLabels[shift.removal_reason!] || shift.removal_reason}
            </div>
          )}
          {showExplanation && (
            <div className="tw:text-xs tw:italic tw:text-zinc-600">
              {shift.explanation && shift.explanation.length > 45 ? (
                <>
                  {shift.explanation.slice(0, 40)}...{' '}
                  <TooltippedText
                    tooltipContent={shift.explanation}
                    tooltipClassName="tw:max-w-[300px]"
                  >
                    <span className="tw:underline tw:cursor-default tw:text-brand-green-1">
                      more
                    </span>
                  </TooltippedText>
                </>
              ) : (
                shift.explanation
              )}
            </div>
          )}
        </div>
      )}

      {/* Bottom row: bonus/holiday badges (front) + modification chips + removed chip */}
      {(showBonus || showHoliday || modBadges.length > 0 || isRemoved) && (
        <div className="tw:flex tw:flex-wrap tw:gap-1 tw:items-center">
          {showBonus && (
            <TooltippedText
              tooltipContent={`Bonus: ${formatCurrencyPostfix(shift.bonus_amount)}`}
              tooltipClassName="tw:bg-blue-100 tw:text-blue-700"
            >
              <button
                type="button"
                className="tw:text-[9px] tw:bg-blue-100 tw:text-blue-700 tw:px-1 tw:!rounded tw:cursor-pointer tw:inline-flex tw:items-center tw:gap-0.5 tw:border-0"
                aria-label="Bonus amount"
              >
                Bonus <i className="far fa-info-circle" />
              </button>
            </TooltippedText>
          )}
          {showHoliday && (
            <TooltippedText
              tooltipContent={`Holiday: ${formatCurrencyPostfix(shift.holiday_amount)}`}
              tooltipClassName="tw:bg-blue-100 tw:text-blue-700"
            >
              <button
                type="button"
                className="tw:text-[9px] tw:bg-blue-100 tw:text-blue-700 tw:px-1 tw:!rounded tw:cursor-pointer tw:inline-flex tw:items-center tw:gap-0.5 tw:border-0"
                aria-label="Holiday amount"
              >
                Holiday <i className="far fa-info-circle" />
              </button>
            </TooltippedText>
          )}
          {modBadges.map((badge) => (
            <span
              key={badge.label}
              className={clsx('tw:text-[9px] tw:px-1 tw:rounded', badge.classes)}
            >
              {badge.label}
            </span>
          ))}
          {isRemoved && (
            <span className="tw:text-[9px] tw:bg-red-100 tw:text-red-700 tw:px-1 tw:rounded">
              Removed
            </span>
          )}
        </div>
      )}
    </div>
  );
};
